/**
 * Thin, dependency-free client for the REST `/api/auth/v1` audience.
 *
 * Deliberately built on plain `fetch` instead of `restClient` (axios) or Apollo:
 * both carry interceptors that assume an established session (attach the stored
 * bearer, tear the session down on 401, trigger a refresh). The refresh and
 * handoff-exchange calls are the very things those interceptors react to, so
 * routing them through the shared transports would recurse. Keeping this module
 * import-free of the transports also keeps it out of their import cycles.
 *
 * Never logs tokens: error messages carry the endpoint + HTTP status only.
 */
import { env } from "@/env";

const AUTH_API_BASE = `${env.backendUrl}/api/auth/v1`;

/** Apps a handoff token can be minted for (backend `LoginApp`). */
export type HandoffTargetApp = "client" | "manager" | "dashboard" | "id";

/**
 * Token-issuing response envelope (`TokenPairDto`). Optional fields are
 * normalised to `null` — cookie mode omits `refreshToken` from the body, and a
 * transitional backend may not send the expiry hints yet.
 */
export type TokenPair = {
  accessToken: string;
  accessTokenExpiresAt: string | null;
  refreshToken: string | null;
  refreshTokenExpiresAt: string | null;
};

/** Failure of an `/api/auth/v1` call. `status` is 0 for network/parse failures. */
export class AuthApiError extends Error {
  public readonly status: number;

  /**
   * Machine-readable error code from the response's `{ error: { code } }`
   * envelope, when the backend sends one; `null` otherwise. Lets callers tell a
   * deliberate denial (e.g. `APP_ACCESS_DENIED`) apart from a bare HTTP status.
   */
  public readonly code: string | null;

  constructor(message: string, status: number, code: string | null = null) {
    super(message);
    this.name = "AuthApiError";
    this.status = status;
    this.code = code;
  }
}

/**
 * True when the failure means the presented credential is dead (rejected or
 * revoked) rather than a transient network/5xx blip. Only these justify tearing
 * a session down — a flaky network must not log the user out.
 */
export const isAuthApiUnauthorized = (error: unknown): boolean =>
  error instanceof AuthApiError && (error.status === 401 || error.status === 403);

/**
 * Backend error code returned, alongside HTTP 403, when the authenticated user
 * has no access to the app a call is scoped to (e.g. minting a Manager handoff
 * for a user who cannot enter Manager).
 */
export const APP_ACCESS_DENIED_CODE = "APP_ACCESS_DENIED";

/**
 * True when a call failed specifically because the backend denied the user
 * access to the requested app (`403 APP_ACCESS_DENIED`) — as opposed to a
 * transient network/5xx blip or an otherwise-invalid session. Callers use it to
 * show a clear "no access" message instead of silently falling back to a legacy
 * link that would let the user in anyway.
 */
export const isAppAccessDenied = (error: unknown): boolean =>
  error instanceof AuthApiError && error.status === 403 && error.code === APP_ACCESS_DENIED_CODE;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const readOptionalString = (value: unknown): string | null =>
  typeof value === "string" && value.length > 0 ? value : null;

/**
 * Best-effort read of the machine-readable error code from a failed response's
 * `{ error: { code } }` envelope. Never throws: a missing, non-JSON, or
 * differently-shaped body just yields `null`, leaving the HTTP status as the
 * only signal.
 */
const readErrorCode = async (response: Response): Promise<string | null> => {
  try {
    const body: unknown = await response.json();
    if (isRecord(body) && isRecord(body.error) && typeof body.error.code === "string") {
      return body.error.code.length > 0 ? body.error.code : null;
    }
  } catch {
    // No body, or not the expected envelope — fall through to null.
  }

  return null;
};

const parseTokenPair = (payload: unknown, path: string): TokenPair => {
  if (!isRecord(payload) || typeof payload.accessToken !== "string" || !payload.accessToken) {
    throw new AuthApiError(`Auth API ${path} returned a malformed token pair`, 0);
  }

  return {
    accessToken: payload.accessToken,
    accessTokenExpiresAt: readOptionalString(payload.accessTokenExpiresAt),
    refreshToken: readOptionalString(payload.refreshToken),
    refreshTokenExpiresAt: readOptionalString(payload.refreshTokenExpiresAt),
  };
};

type PostOptions = {
  /** Bearer token for the endpoints that require an established session. */
  accessToken?: string | null;
  /** Let the request outlive the page (logout fires during teardown). */
  keepalive?: boolean;
};

const post = async (path: string, body: unknown, options: PostOptions = {}): Promise<unknown> => {
  let response: Response;

  try {
    response = await fetch(`${AUTH_API_BASE}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-app-source": "dashboard",
        ...(options.accessToken ? { Authorization: `Bearer ${options.accessToken}` } : {}),
      },
      body: JSON.stringify(body),
      keepalive: options.keepalive,
    });
  } catch {
    // Network-level failure — no status to report, and explicitly NOT an
    // authentication failure, so callers keep the session.
    throw new AuthApiError(`Auth API ${path} could not be reached`, 0);
  }

  if (!response.ok) {
    throw new AuthApiError(
      `Auth API ${path} failed`,
      response.status,
      await readErrorCode(response)
    );
  }

  if (response.status === 204) return null;

  try {
    return await response.json();
  } catch {
    throw new AuthApiError(`Auth API ${path} returned an unreadable body`, 0);
  }
};

/**
 * Redeems the single-use cross-app handoff token the ID portal (or another
 * Tournated app) hands over in the URL fragment.
 *
 * @throws {AuthApiError} when the token is expired, already redeemed, or the
 * request fails.
 */
export const exchangeHandoffToken = async (handoffToken: string): Promise<TokenPair> =>
  parseTokenPair(await post("/handoff/exchange", { handoffToken }), "/handoff/exchange");

/**
 * Rotates the refresh token, returning a fresh pair. The presented token is
 * consumed — the caller MUST persist the returned `refreshToken`.
 *
 * @throws {AuthApiError} 401 when the token is expired/revoked/reused (the
 * whole family is then revoked server-side).
 */
export const refreshTokenPair = async (refreshToken: string): Promise<TokenPair> =>
  parseTokenPair(await post("/refresh", { refreshToken }), "/refresh");

/**
 * Upgrades a legacy bearer-only session (pre-refresh-token signer, no `typ`
 * claim) to an access + refresh pair scoped to this app. The presented access
 * token is untouched server-side — callers keep the legacy session on failure.
 *
 * @throws {AuthApiError} when the token is rejected or the request fails.
 */
export const upgradeSession = async (accessToken: string): Promise<TokenPair> =>
  parseTokenPair(
    await post("/session/upgrade", { app: "dashboard" }, { accessToken }),
    "/session/upgrade"
  );

/**
 * Mints a short-lived (60s), single-use handoff token for another Tournated app
 * so a cross-app link never has to carry the access token in its URL.
 *
 * @throws {AuthApiError} 401/403 when the session is invalid or the user has no
 * access to `targetApp`. A denial carries `status: 403` and
 * `code: {@link APP_ACCESS_DENIED_CODE}` — use {@link isAppAccessDenied} to tell
 * it apart from a transient failure before deciding whether to fall back.
 */
export const mintHandoffToken = async ({
  targetApp,
  accessToken,
}: {
  targetApp: HandoffTargetApp;
  accessToken: string;
}): Promise<string> => {
  const payload = await post("/handoff", { targetApp }, { accessToken });

  if (!isRecord(payload) || typeof payload.handoffToken !== "string" || !payload.handoffToken) {
    throw new AuthApiError("Auth API /handoff returned a malformed handoff token", 0);
  }

  return payload.handoffToken;
};

/**
 * Best-effort server-side revocation of the refresh-token family + session.
 * Resolves even on failure — local teardown must never be blocked by it.
 */
export const revokeRefreshToken = async (refreshToken: string): Promise<void> => {
  try {
    await post("/logout", { refreshToken }, { keepalive: true });
  } catch {
    // Intentionally ignored: the local session is torn down regardless, and the
    // refresh token expires on its own.
  }
};
